import { Navigate, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useStorageNode, useStorageRoots } from '@/hooks/useStorage'
import { unitPageHref } from '@/lib/storageUnitView'

/**
 * `/inventory/storage/node/:nodeId` — send a storage POSITION to the unit page
 * that can show it.
 *
 * An audit row names the exact node a bag was put in ("LN2-A/R1/B1/A1"), but
 * `/inventory/storage/:unitId` addresses the ROOT unit: it looks the id up in
 * the tree it loaded, and a node id from four levels down matches nothing.
 *
 * Resolving one to the other needs two reads — the node, for its materialised
 * path, and the tenant's roots, to turn that path's first segment into an id —
 * so it cannot happen inside a table cell. This route is where an audit link
 * points instead, which keeps `src/lib/auditLinks.ts` pure and keeps the lookup
 * out of every row.
 *
 * ClickUp z8nrz7dxeu.
 */
export function StorageNodeRedirectPage() {
  const { nodeId } = useParams<{ nodeId: string }>()
  const node = useStorageNode(nodeId ?? null)
  const roots = useStorageRoots()

  if (node.isLoading || roots.isLoading) {
    return (
      <div className='flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground'>
        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
        Finding this location…
      </div>
    )
  }

  // The unit is the first segment of the node's own path. A root's `path` IS
  // that segment, so the match is against the path rather than the code —
  // `code` is an address within a parent, and a root has no parent.
  const unitPath = node.data?.path.split('/')[0]
  const unit = unitPath ? roots.data?.find(r => r.path === unitPath) : undefined

  // `?node=` selects the exact node on arrival, its box drawn around it.
  if (unit && node.data) {
    return <Navigate to={unitPageHref('inventory', unit.id, { node: node.data.id })} replace />
  }

  /*
   * Deleted, or in another tenant after a switch. Sending them to the storage
   * list rather than a dead unit page: the node the audit row names is a real
   * historical fact, but the place it lived may not exist any more, and an
   * empty freezer layout would read as the link being broken rather than as
   * the location being gone.
   */
  return <Navigate to='/inventory?tab=storage' replace />
}
